import { useMemo, useState } from 'react';
import {
  V2Badge,
  V2Button,
  V2DataGrid,
  V2EmptyState,
  type V2TableColumn,
} from '@sakhtar/design-system/react';
import { ApiError, apiPut } from '../../lib/apiClient.js';
import { faDigits } from '../../lib/faDigits.js';
import { toEnglishDigits } from '../../lib/phoneNumber.js';
import type { CommissionRule } from '../../lib/types/commission.js';

const PERCENT = /^\d{1,3}(\.\d{1,2})?$/;

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'ذخیره انجام نشد؛ دوباره تلاش کنید.';
}

interface RuleRow extends CommissionRule {
  readonly id: string;
}

export function CommissionRulesPanel({
  base,
  rules,
  configured,
  query,
  onQuery,
  onSaved,
}: {
  readonly base: string;
  readonly rules: readonly CommissionRule[];
  readonly configured: boolean;
  readonly query: string;
  readonly onQuery: (value: string) => void;
  readonly onSaved: () => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});

  const filtered = useMemo(() => {
    const value = toEnglishDigits(query).trim().toLowerCase();
    if (!value) return rules;
    return rules.filter(
      (rule) =>
        rule.productCode.toLowerCase().includes(value) ||
        rule.productName.toLowerCase().includes(value),
    );
  }, [rules, query]);

  async function save(rule: CommissionRule, status: 'active' | 'inactive') {
    const raw = toEnglishDigits(drafts[rule.productId] ?? rule.ratePercent ?? '').trim();
    if (!PERCENT.test(raw) || Number(raw) > 100) {
      setRowError((prev) => ({
        ...prev,
        [rule.productId]: 'درصد باید بین ۰ تا ۱۰۰ و حداکثر با دو رقم اعشار باشد.',
      }));
      return;
    }
    setSavingId(rule.productId);
    setRowError((prev) => {
      const next = { ...prev };
      delete next[rule.productId];
      return next;
    });
    try {
      await apiPut(`${base}/api/v1/sales/commission/rules/${rule.productId}`, {
        ratePercent: raw,
        status,
      });
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[rule.productId];
        return next;
      });
      onSaved();
    } catch (error) {
      setRowError((prev) => ({ ...prev, [rule.productId]: errorMessage(error) }));
    } finally {
      setSavingId(null);
    }
  }

  const columns: V2TableColumn<RuleRow>[] = [
    {
      key: 'code',
      header: 'کد کالا',
      render: (row) => <span className="ltr">{row.productCode}</span>,
    },
    { key: 'name', header: 'نام کالا', render: (row) => row.productName },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) =>
        row.status === 'active' && row.ratePercent !== '0' ? (
          <V2Badge tone="success">فعال</V2Badge>
        ) : row.status === 'inactive' ? (
          <V2Badge tone="neutral">غیرفعال</V2Badge>
        ) : (
          <V2Badge tone="neutral">بدون پورسانت</V2Badge>
        ),
    },
    {
      key: 'rate',
      header: 'درصد پورسانت',
      align: 'end',
      render: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              className="sk-input ltr sk-num"
              style={{ width: 84 }}
              inputMode="decimal"
              aria-label={`درصد پورسانت ${row.productName}`}
              placeholder="۰"
              value={drafts[row.productId] ?? (row.ratePercent ? faDigits(row.ratePercent) : '')}
              onChange={(event) =>
                setDrafts((prev) => ({ ...prev, [row.productId]: event.target.value }))
              }
            />
            <span style={{ color: 'var(--sk-fg-muted)' }}>٪</span>
          </div>
          {rowError[row.productId] ? (
            <span role="alert" style={{ fontSize: 11, color: 'var(--sk-danger)' }}>
              {rowError[row.productId]}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (row) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <V2Button
            size="sm"
            variant="primary"
            disabled={savingId === row.productId}
            onClick={() => void save(row, 'active')}
          >
            ذخیره
          </V2Button>
          {row.status === 'active' ? (
            <V2Button
              size="sm"
              variant="secondary"
              disabled={savingId === row.productId}
              onClick={() => void save(row, 'inactive')}
            >
              غیرفعال
            </V2Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <V2DataGrid
      icon="tag"
      title="پورسانت محصولات"
      subtitle={
        configured
          ? 'درصد از مبلغ فروش هر ردیف فاکتور (بدون مالیات بر ارزش افزوده)'
          : 'حساب‌های سند پورسانت باید ابتدا در مالی تنظیم شود؛ بعد می‌توانید پورسانت فعال کنید.'
      }
      search={{ value: query, onChange: onQuery, placeholder: 'کد یا نام کالا' }}
      columns={columns}
      rows={filtered.map((rule) => ({ ...rule, id: rule.productId }))}
      rowKey={(row) => row.id}
      zebra
      emptyState={
        <V2EmptyState
          icon="tag"
          title={query ? 'کالایی با این جست‌وجو پیدا نشد' : 'کالای فعالی تعریف نشده است'}
          description="کالاها از اطلاعات پایه تعریف می‌شوند."
        />
      }
    />
  );
}
